import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import LoadingBar from '../alerts/LoadingBar'
import UploadSummaryModal from './UploadSummaryModal'
import { FetchFileStatusState, FileStatus, GroupModel } from './interface/Group'
import { useStreamingState } from './hooks/UseStreamingState'
import { BASE_SERVICE_URL } from '../../lib/constants'

import '../main/options/modalFull.scss'

interface Props {
    showDialog: (show: boolean) => void
    files: File[]
    groupModel: GroupModel
}

type Step = 'uploading' | 'checking' | 'summary'

export default function MultipleUploadsDialog({ showDialog, files, groupModel }: Props) {
    const [step, setStep] = useState<Step>('uploading')
    const [amountOfFiles] = useState<number>(files.length)
    const [showSummaryModal, setShowSummaryModal] = useState<boolean>(false)
    const [filesStatus, setFilesStatus] = useState<FetchFileStatusState>({ done: false, status: [] })

    const handleStep = (step: Step) => {
        switch (step) {
            case 'uploading': {
                const formData = new FormData()
                files.forEach(file => {
                    formData.append(file.name, file)
                })

                useStreamingState(
                    {
                        reqInfo: `${BASE_SERVICE_URL}/uploadgroupfiles`,
                        reqInit: {
                            method: 'POST',
                            body: formData
                        },
                        streamBuffer: true,
                        dataSeparator: '\\n'
                    },
                    stream => {
                        const newFilesStatus: FileStatus[] = []
                        stream.data.forEach(data => {
                            newFilesStatus.push(JSON.parse(data))
                        })

                        setFilesStatus({
                            done: stream.done,
                            status: [...newFilesStatus]
                        })

                        if (stream.done) {
                            setStep('checking')
                        }
                    }
                )

                break
            }
            case 'checking': {
                const requestBody = {
                    groupUID: groupModel.uid,
                    filesStatus: [...filesStatus.status]
                }

                useStreamingState(
                    {
                        reqInfo: `${BASE_SERVICE_URL}/checkgroupfiles`,
                        reqInit: {
                            method: 'POST',
                            body: JSON.stringify(requestBody),
                            headers: {
                                'Content-Type': 'application/json'
                            }
                        },
                        streamBuffer: true,
                        dataSeparator: '\\n'
                    },
                    stream => {
                        const newFilesStatus: FileStatus[] = []

                        stream.data.forEach(fileStatus => {
                            newFilesStatus.push(JSON.parse(fileStatus))
                        })

                        setFilesStatus({
                            done: stream.done,
                            status: [...newFilesStatus]
                        })

                        if (stream.done) {
                            setStep('summary')
                            setShowSummaryModal(true)
                        }
                    }
                )

                break
            }
            default:
                break
        }
    }

    useEffect(() => {
        handleStep(step)
    }, [step])

    const cleanUp = () => {
        setShowSummaryModal(false)
        showDialog(false)
    }

    const finishProcessData = () => {
        cleanUp()
    }

    return (
        <>
            {step !== 'summary' ? (
                <LoadingContainer>
                    <LoadingBar
                        currProgress={filesStatus.status.length}
                        targetProgress={amountOfFiles}
                        title={(() => {
                            switch (step) {
                                case 'uploading':
                                    return 'Files are being uploaded to the server'
                                case 'checking':
                                    return 'Files are being checked'
                                default:
                                    return
                            }
                        })()}
                        status={'loading'}
                        color={'#37A3EF'}
                    />
                </LoadingContainer>
            ) : null}

            {step === 'summary' && (
                <UploadSummaryModal
                    showModal={showSummaryModal}
                    setShowModal={setShowSummaryModal}
                    handleDiscard={cleanUp}
                    checkedFiles={[...filesStatus.status]}
                    finishProcessData={finishProcessData}
                    groupModel={groupModel}
                    step={step}
                />
            )}
        </>
    )
}

const FooterContainer = styled.div`
    .button {
        &--save {
            width: 79px;
            height: 32px;
            color: white;
            background: #37a3ef;
            border: none;
            border-radius: 3px;
            font-family: Source Sans Pro, Open Sans Pro, Arial, Helvetica, sans-serif;
            margin-right: 16px;
        }
        &--cancel {
            width: 79px;
            height: 32px;
            border: 1px solid #c8ccd4;
            border-radius: 3px;
            font-family: Source Sans Pro, Open Sans Pro, Arial, Helvetica, sans-serif;
        }
    }
`
const LoadingContainer = styled.div`
    margin: 0 auto;
    padding: 16px;
    box-shadow: 0px 18px 16px -12px #36415938;
    position: relative;
    border: 1px solid #dddee1;
    position: absolute;
    width: 632px;
    height: 88px;
    left: 35%;
    right: 35%;
    top: 1px;
    z-index: 50;
    background: white;
`
